import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCheck } from 'lucide-react';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import { formatTaka } from '@/utils/number';
import { PCMP_CORRECTION_ACTION_COPY } from '../../constants/pcmpCorrection.constants';
import {
  PCMP_MKT_COMMENT_MAX,
  PCMP_PAYMENT_TYPE_LABELS,
} from '../../constants/pcmpProposal.constants';
import { useApprovePcmpProposalCorrection } from '../../hooks/useApprovePcmpProposalCorrection';
import type { PcmpProposalDetail } from '../../types/pcmpProposal.types';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpPaymentAccountDetails } from '../PcmpProposalDetail/PcmpPaymentCard';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';
import { PcmpProposalDialogLoader } from './PcmpProposalDialogLoader';

const approveFormSchema = z.object({
  comment: z.string().trim().min(1, 'A comment is required.').max(PCMP_MKT_COMMENT_MAX),
});

type ApproveFormValues = z.infer<typeof approveFormSchema>;

interface PcmpCorrectionApproveDialogProps {
  proposalId: number;
  onClose: () => void;
}

/** Legacy CorrectionApprove: the payment is Corrected and waits for re-disbursement. */
export function PcmpCorrectionApproveDialog({
  proposalId,
  onClose,
}: PcmpCorrectionApproveDialogProps) {
  return (
    <PcmpProposalDialogLoader
      proposalId={proposalId}
      title={PCMP_CORRECTION_ACTION_COPY.APPROVE.title}
      onClose={onClose}
    >
      {(proposal) => <ApproveForm proposal={proposal} onClose={onClose} />}
    </PcmpProposalDialogLoader>
  );
}

function ApproveForm({ proposal, onClose }: { proposal: PcmpProposalDetail; onClose: () => void }) {
  const form = useForm<ApproveFormValues>({
    resolver: zodResolver(approveFormSchema),
    defaultValues: { comment: '' },
  });
  const { mutate, isPending, error } = useApprovePcmpProposalCorrection(
    proposal.proposalId,
    onClose,
  );
  const copy = PCMP_CORRECTION_ACTION_COPY.APPROVE;
  const { payment } = proposal;

  const onSubmit = form.handleSubmit((values) => mutate({ comment: values.comment }));

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={copy.title}
        description={proposal.refNo}
        icon={CheckCheck}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel={copy.submit}
        pendingLabel="Approving..."
        className="sm:max-w-2xl"
      >
        <div className="flex flex-col gap-3 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          <span className="text-xs text-muted-foreground">
            To be paid {formatTaka(payment.proposedAmount)} by{' '}
            {PCMP_PAYMENT_TYPE_LABELS[payment.paymentType]}
          </span>
          <PcmpPaymentAccountDetails payment={payment} />
        </div>
        <p className="text-sm text-muted-foreground">
          Marks the payment Corrected. Accounts re-disburses it from the Reverified tab. Marketing
          and MIS approvals are untouched.
        </p>
        <PcmpCommentField control={form.control} name="comment" label="Comment" required />
      </PcmpEditDialog>
    </Form>
  );
}
